import type { ReactNode } from 'react';
import { API_BASE_URL, type ApiError } from '../api/client';

export function LoadingState({ label = 'Loading…', rows = 3 }: { label?: string; rows?: number }) {
  return (
    <div className="state state--loading" role="status" aria-live="polite">
      <span className="sr-only">{label}</span>
      <div className="skeleton-stack" aria-hidden="true">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="skeleton" />
        ))}
      </div>
    </div>
  );
}

interface EmptyStateProps {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  glyph?: string;
}

export function EmptyState({ title, children, action, glyph = '◍' }: EmptyStateProps) {
  return (
    <div className="state state--empty">
      <div className="state__glyph" aria-hidden="true">
        {glyph}
      </div>
      <h3 className="state__title">{title}</h3>
      {children ? <div className="state__body">{children}</div> : null}
      {action ? <div className="state__action">{action}</div> : null}
    </div>
  );
}

interface ErrorStateProps {
  error: ApiError;
  onRetry?: () => void;
  /** What was being loaded, e.g. "the event feed". */
  context?: string;
}

/**
 * "API unreachable" gets its own layout rather than a generic red box: with the
 * dashboard on Vercel and the API on Fly, a misconfigured VITE_API_BASE_URL or a
 * sleeping machine is by far the most likely thing a first-time visitor sees.
 */
export function ErrorState({ error, onRetry, context }: ErrorStateProps) {
  if (error.isUnreachable) {
    return (
      <div className="state state--error">
        <div className="state__glyph state__glyph--danger" aria-hidden="true">
          ⚠
        </div>
        <h3 className="state__title">Can’t reach the Relay API</h3>
        <p className="state__body">
          The dashboard is running, but nothing answered at{' '}
          <code className="mono">{API_BASE_URL}</code>.
        </p>
        <ul className="state__checklist">
          <li>
            Is the API up? <code className="mono">npm run dev:api</code> from the repo root.
          </li>
          <li>
            Does <code className="mono">VITE_API_BASE_URL</code> point at the right origin? It is baked
            in at build time, so changing it needs a restart or a redeploy.
          </li>
          <li>Is the API sending CORS headers for this origin?</li>
        </ul>
        {error.detail ? <p className="state__detail mono">{error.detail}</p> : null}
        {onRetry ? (
          <div className="state__action">
            <button type="button" className="btn btn--primary" onClick={onRetry}>
              Try again
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  const title =
    error.kind === 'config'
      ? 'Dashboard not configured'
      : error.status === 404
        ? 'Not found'
        : `Couldn’t load ${context ?? 'this view'}`;

  return (
    <div className="state state--error">
      <div className="state__glyph state__glyph--danger" aria-hidden="true">
        ⚠
      </div>
      <h3 className="state__title">{title}</h3>
      <p className="state__body">{error.message}</p>
      {error.detail ? <p className="state__detail mono">{error.detail}</p> : null}
      {onRetry ? (
        <div className="state__action">
          <button type="button" className="btn btn--primary" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Inline, non-blocking variant for when stale data is still on screen. */
export function ErrorBanner({ error, onRetry }: { error: ApiError; onRetry?: () => void }) {
  return (
    <div className="error-banner" role="alert">
      <span className="error-banner__glyph" aria-hidden="true">
        ⚠
      </span>
      <span className="error-banner__text">
        {error.isUnreachable ? `Lost contact with ${API_BASE_URL}` : error.message}
        {error.detail ? <span className="error-banner__detail"> — {error.detail}</span> : null}
      </span>
      {onRetry ? (
        <button type="button" className="btn btn--ghost btn--sm" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  );
}
